import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faBolt,
  faBox,
  faCalendarDay,
  faCheck,
  faCheckCircle,
  faCreditCard,
  faDollarSign,
  faExternalLinkAlt,
  faFileInvoice,
  faIdCard,
  faListCheck,
  faMapLocationDot,
  faMapMarkerAlt,
  faPen,
  faRoute,
  faShieldHalved,
  faTemperatureHalf,
  faTruck
} from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom, combineLatest, debounceTime, distinctUntilChanged } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DividerModule } from 'primeng/divider';
import { FieldsetModule } from 'primeng/fieldset';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { StepsModule } from 'primeng/steps';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { GoogleMapComponent } from '../../shared/google-map/google-map.component';
import { SessionStorageService } from '../../helpers/session-storage.service';
import { Country } from '../../models/common/country.model';
import { SharedEndpointService } from '../../services/shared-endpoint.service';
import { MarketplaceAnalyticsService } from '../marketplace-analytics.service';
import { LoadRequestPayload, LoadRequestStatus } from '../load-request.model';
import { LoadRequestService } from '../load-request.service';
import { LanguageService } from '../../services/language.service';
import { CurrencyResolverService } from '../../services/currency-resolver.service';

interface ReviewGroup {
  label: string;
  items: { label: string; value: string }[];
}

@Component({
  selector: 'app-create-load',
  templateUrl: './create-load.component.html',
  styleUrls: ['./create-load.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule,
    InputNumberModule,
    DividerModule,
    StepsModule,
    TagModule,
    TableModule,
    FloatLabelModule,
    TooltipModule,
    CheckboxModule,
    IftaLabelModule,
    FieldsetModule,
    DatePickerModule,
    TextareaModule,
    RouterModule,
    GoogleMapComponent,
    TranslateModule,
    FontAwesomeModule
  ]
})
export class CreateLoadComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly loadRequestService = inject(LoadRequestService);
  private readonly sharedService = inject(SharedEndpointService);
  private readonly analyticsService = inject(MarketplaceAnalyticsService);
  private readonly sessionStorage = inject(SessionStorageService);
  protected readonly currencyResolver = inject(CurrencyResolverService);
  readonly languageService = inject(LanguageService);

  readonly steps = signal<MenuItem[]>([
    { label: this.ui('Lane & Schedule', 'المسار والجدولة') },
    { label: this.ui('Equipment', 'المعدات') },
    { label: this.ui('Cargo', 'البضاعة') },
    { label: this.ui('Commercial & Contact', 'التسعير والتواصل') },
    { label: this.ui('Review', 'المراجعة') }
  ]);
  readonly pickupLocation = signal<string>('');
  readonly deliveryLocation = signal<string>('');
  readonly pricingContext = signal<{ label: string; value: string }[]>([]);
  readonly isSubmitting = signal(false);

  readonly icons = {
    arrowLeft: faArrowLeft,
    arrowRight: faArrowRight,
    bolt: faBolt,
    box: faBox,
    calendar: faCalendarDay,
    check: faCheck,
    checkCircle: faCheckCircle,
    creditCard: faCreditCard,
    dollar: faDollarSign,
    external: faExternalLinkAlt,
    invoice: faFileInvoice,
    listCheck: faListCheck,
    map: faMapLocationDot,
    marker: faMapMarkerAlt,
    pen: faPen,
    reference: faIdCard,
    route: faRoute,
    shield: faShieldHalved,
    temperature: faTemperatureHalf,
    truck: faTruck
  };

  readonly activeStepLabels = [
    this.ui('Lane & Schedule', 'المسار والجدولة'),
    this.ui('Equipment', 'المعدات'),
    this.ui('Cargo', 'البضاعة'),
    this.ui('Commercial & Contact', 'التسعير والتواصل'),
    this.ui('Review', 'المراجعة')
  ];

  activeStep = 0;
  private readonly attemptedSteps = new Set<number>();

  readonly laneForm: FormGroup = this.fb.group({
    origin: [null, Validators.required],
    destination: [null, Validators.required],
    via: [null],
    pickupDate: [null, Validators.required],
    pickupWindowStart: [null],
    pickupWindowEnd: [null],
    deliveryDate: [null],
    totalDistanceInKm: [null],
    referenceNumber: [null]
  });

  readonly equipmentForm: FormGroup = this.fb.group({
    equipment: ['Dry Van', Validators.required],
    equipmentCount: [1, [Validators.required, Validators.min(1)]],
    vehicleLength: [null],
    vehicleWidth: [null],
    vehicleHeight: [null],
    vehicleVolume: [null],
    temperatureControlled: [false]
  });

  readonly cargoForm: FormGroup = this.fb.group({
    commodity: [null],
    quantity: [null],
    pallets: [null],
    weightKg: [null],
    insuranceRequired: [true],
    notes: [null]
  });

  readonly commercialForm: FormGroup = this.fb.group({
    targetRate: [null, [Validators.required, Validators.min(0.01)]],
    currencyCode: [this.currencyResolver.currentFallbackCurrencyCode(), Validators.required],
    paymentTerms: ['Immediate'],
    contactName: [null],
    contactNumber: [null, Validators.required],
    clientCompanyName: [null]
  });

  constructor() {
    this.initCompanyData();

    this.laneForm.get('origin')?.valueChanges.pipe(takeUntilDestroyed()).subscribe(val => {
      this.pickupLocation.set(val || '');
    });
    this.laneForm.get('destination')?.valueChanges.pipe(takeUntilDestroyed()).subscribe(val => {
      this.deliveryLocation.set(val || '');
    });

    combineLatest([
      this.laneForm.get('origin')!.valueChanges.pipe(debounceTime(500), distinctUntilChanged()),
      this.laneForm.get('destination')!.valueChanges.pipe(debounceTime(500), distinctUntilChanged())
    ]).pipe(takeUntilDestroyed()).subscribe(([origin, destination]) => {
      if (origin && destination) {
        this.updateMarketIntelligence(origin, destination);
      } else {
        this.resetMarketIntelligence();
      }
    });

    this.resetMarketIntelligence();
  }

  get currentActionLabel(): string {
    return this.activeStep === this.steps().length - 1 ? this.ui('Publish Load', 'نشر الشحنة') : this.ui('Next Step', 'الخطوة التالية');
  }

  get currentActionIcon() {
    return this.activeStep === this.steps().length - 1 ? this.icons.check : this.icons.arrowRight;
  }

  get reviewGroups(): ReviewGroup[] {
    const lane = this.laneForm.value;
    const equipment = this.equipmentForm.value;
    const cargo = this.cargoForm.value;
    const commercial = this.commercialForm.value;
    const currency = this.currencyResolver.effectiveCurrencyCode(commercial.currencyCode);

    return [
      {
        label: this.ui('Lane & Schedule', 'المسار والجدولة'),
        items: [
          { label: this.ui('Origin', 'مصدر الاستلام'), value: this.display(lane.origin) },
          { label: this.ui('Destination', 'وجهة التسليم'), value: this.display(lane.destination) },
          { label: this.ui('Via', 'عبر'), value: this.displayList(this.splitList(lane.via)) },
          { label: this.ui('Pickup Date', 'تاريخ الاستلام'), value: this.display(this.formatDate(lane.pickupDate)) },
          { label: this.ui('Pickup Window', 'نافذة الاستلام'), value: this.display(this.formatWindow(lane.pickupWindowStart, lane.pickupWindowEnd)) },
          { label: this.ui('Delivery Date', 'تاريخ التسليم'), value: this.display(this.formatDate(lane.deliveryDate)) },
          { label: this.ui('Distance', 'المسافة'), value: lane.totalDistanceInKm ? `${lane.totalDistanceInKm} km` : '-' },
          { label: this.ui('Reference', 'المرجع'), value: this.display(lane.referenceNumber) }
        ]
      },
      {
        label: this.ui('Equipment', 'المعدات'),
        items: [
          { label: this.ui('Equipment', 'المعدة'), value: this.display(equipment.equipment) },
          { label: this.ui('Equipment Count', 'عدد المعدات'), value: this.display(equipment.equipmentCount) },
          { label: this.ui('Length', 'الطول'), value: equipment.vehicleLength ? `${equipment.vehicleLength} m` : '-' },
          { label: this.ui('Width', 'العرض'), value: equipment.vehicleWidth ? `${equipment.vehicleWidth} m` : '-' },
          { label: this.ui('Height', 'الارتفاع'), value: equipment.vehicleHeight ? `${equipment.vehicleHeight} m` : '-' },
          { label: this.ui('Volume', 'الحجم'), value: equipment.vehicleVolume ? `${equipment.vehicleVolume} CBM` : '-' },
          { label: this.ui('Temperature Controlled', 'تحكم بالحرارة'), value: equipment.temperatureControlled ? this.ui('Yes', 'نعم') : this.ui('No', 'لا') }
        ]
      },
      {
        label: this.ui('Cargo', 'البضاعة'),
        items: [
          { label: this.ui('Commodity', 'البضاعة'), value: this.display(cargo.commodity) },
          { label: this.ui('Quantity', 'الكمية'), value: this.display(cargo.quantity) },
          { label: this.ui('Pallets', 'المنصات'), value: this.display(cargo.pallets) },
          { label: this.ui('Weight', 'الوزن'), value: cargo.weightKg ? `${cargo.weightKg} kg` : '-' },
          { label: this.ui('Insurance Required', 'التأمين مطلوب'), value: cargo.insuranceRequired ? this.ui('Required', 'مطلوب') : this.ui('Not required', 'غير مطلوب') },
          { label: this.ui('Notes', 'ملاحظات'), value: this.displayList(this.splitLines(cargo.notes)) }
        ]
      },
      {
        label: this.ui('Commercial & Contact', 'التسعير والتواصل'),
        items: [
          { label: this.ui('Target Rate', 'السعر المستهدف'), value: commercial.targetRate ? `${Number(commercial.targetRate).toLocaleString()} ${currency}` : '-' },
          { label: this.ui('Payment Terms', 'شروط الدفع'), value: this.display(commercial.paymentTerms) },
          { label: this.ui('Contact Name', 'اسم جهة الاتصال'), value: this.display(commercial.contactName) },
          { label: this.ui('Contact Number', 'رقم التواصل'), value: this.display(commercial.contactNumber) },
          { label: this.ui('Client Company', 'شركة العميل'), value: this.display(commercial.clientCompanyName) }
        ]
      }
    ];
  }

  nextStep(): void {
    if (!this.validateStep(this.activeStep)) {
      return;
    }

    if (this.activeStep < this.steps().length - 1) {
      this.activeStep += 1;
    }
  }

  ui(en: string, ar: string): string {
    return this.languageService.currentLanguage() === 'ar' ? ar : en;
  }

  previousStep(): void {
    if (this.activeStep > 0) {
      this.activeStep -= 1;
    }
  }

  publishLoad(): void {
    if (!this.validateAllSteps()) {
      const firstInvalidStep = [this.laneForm, this.equipmentForm, this.cargoForm, this.commercialForm]
        .findIndex(form => form.invalid);
      this.activeStep = firstInvalidStep >= 0 ? firstInvalidStep : this.activeStep;
      return;
    }

    const lane = this.laneForm.value;
    const equipment = this.equipmentForm.value;
    const cargo = this.cargoForm.value;
    const commercial = this.commercialForm.value;
    const selectedEquipment = this.cleanText(equipment.equipment);
    const companyId = this.sessionStorage.getCompany()?.id ?? 0;

    const payload: LoadRequestPayload = {
      shipperCompanyId: companyId,
      referenceNumber: this.cleanText(lane.referenceNumber),
      origin: this.cleanText(lane.origin) ?? '',
      destination: this.cleanText(lane.destination) ?? '',
      via: this.splitList(lane.via),
      pickupDate: this.formatDate(lane.pickupDate),
      deliveryDate: this.formatDate(lane.deliveryDate),
      pickupWindow: this.formatWindow(lane.pickupWindowStart, lane.pickupWindowEnd),
      pickupWindowStart: this.formatTime(lane.pickupWindowStart),
      pickupWindowEnd: this.formatTime(lane.pickupWindowEnd),
      equipmentType: selectedEquipment,
      loadType: null,
      offerType: null,
      vehicleType: selectedEquipment ? [selectedEquipment] : [],
      vehicleConfig: selectedEquipment ? [selectedEquipment] : [],
      vehicleLength: this.toNumber(equipment.vehicleLength),
      vehicleWidth: this.toNumber(equipment.vehicleWidth),
      vehicleHeight: this.toNumber(equipment.vehicleHeight),
      vehicleVolume: this.toNumber(equipment.vehicleVolume),
      pallets: this.toNumber(cargo.pallets),
      equipmentCount: this.toNumber(equipment.equipmentCount),
      quantity: this.cleanText(cargo.quantity),
      weightLbs: null,
      weightKg: this.toNumber(cargo.weightKg),
      targetRate: this.toNumber(commercial.targetRate),
      currencyCode: this.currencyResolver.effectiveCurrencyCode(this.cleanText(commercial.currencyCode)),
      totalDistanceInKm: this.toNumber(lane.totalDistanceInKm),
      commodity: this.cleanText(cargo.commodity),
      temperatureControlled: Boolean(equipment.temperatureControlled),
      paymentTerms: this.cleanText(commercial.paymentTerms),
      contactName: this.cleanText(commercial.contactName),
      contactNumber: this.cleanText(commercial.contactNumber),
      sourceLanguage: 'en',
      clientCompanyName: this.cleanText(commercial.clientCompanyName),
      insuranceRequired: Boolean(cargo.insuranceRequired),
      notes: this.splitLines(cargo.notes),
      missingFields: [],
      redFlags: [],
      status: LoadRequestStatus.POSTED
    };

    this.isSubmitting.set(true);
    this.loadRequestService.create(payload).subscribe({
      next: response => {
        this.isSubmitting.set(false);
        this.router.navigate(['/load-details', response.id]);
      },
      error: error => {
        this.isSubmitting.set(false);
        console.error('Load publish failed', error);
      }
    });
  }

  submitCurrentAction(): void {
    if (this.activeStep === this.steps().length - 1) {
      this.publishLoad();
      return;
    }
    this.nextStep();
  }

  showError(form: FormGroup, controlName: string): boolean {
    const control = form.get(controlName);
    return Boolean(control?.invalid && (control.touched || this.attemptedSteps.has(this.activeStep)));
  }

  private validateStep(step: number): boolean {
    this.attemptedSteps.add(step);
    const form = this.formForStep(step);
    if (!form) {
      return true;
    }
    form.markAllAsTouched();
    return form.valid;
  }

  private validateAllSteps(): boolean {
    [0, 1, 2, 3].forEach(step => this.attemptedSteps.add(step));
    [this.laneForm, this.equipmentForm, this.cargoForm, this.commercialForm].forEach(form => form.markAllAsTouched());
    return this.laneForm.valid && this.equipmentForm.valid && this.cargoForm.valid && this.commercialForm.valid;
  }

  private formForStep(step: number): FormGroup | null {
    return [this.laneForm, this.equipmentForm, this.cargoForm, this.commercialForm][step] ?? null;
  }

  private async initCompanyData(): Promise<void> {
    try {
      const company = this.sessionStorage.getCompany();
      if (!company) {
        return;
      }

      if (company.countryId) {
        const countries = await firstValueFrom(this.sharedService.findAllCountries('en-US')) as Country[];
        const country = countries.find(c => c.id === company.countryId);
        if (country?.currencyCode) {
          this.commercialForm.patchValue({ currencyCode: country.currencyCode });
        }
      }

      this.commercialForm.patchValue({
        paymentTerms: company.enableEnterpriseFeatures === true ? 'Net 30' : 'Immediate'
      });
    } catch (error) {
      console.error('Error initializing company data:', error);
    }
  }

  private resetMarketIntelligence(): void {
    this.pricingContext.set([
      { label: 'Avg accepted', value: 'N/A' },
      { label: 'Lane RPM', value: 'N/A' },
      { label: 'Win range', value: 'N/A' },
      { label: 'Carrier interest', value: 'N/A' },
      { label: 'Deadhead risk', value: 'N/A' },
      { label: 'Requirement norms', value: 'N/A' },
      { label: 'Payment preference', value: 'N/A' },
      { label: 'Matching capacity', value: 'N/A' },
      { label: 'Historical loads', value: 'N/A' }
    ]);
  }

  private async updateMarketIntelligence(origin: string, destination: string): Promise<void> {
    try {
      const intel = await firstValueFrom(this.analyticsService.getLaneIntelligence(origin, destination));
      const currency = this.currencyResolver.effectiveCurrencyCode(this.commercialForm.get('currencyCode')?.value);
      const hasHistory = intel.sampleSize > 0;

      this.pricingContext.set([
        { label: 'Avg accepted', value: hasHistory ? `${intel.averageRate.toLocaleString()} ${currency}` : 'N/A' },
        { label: 'Lane RPM', value: hasHistory && intel.rpm > 0 ? `${intel.rpm.toFixed(2)} ${currency}/km` : 'N/A' },
        { label: 'Win range', value: hasHistory ? `${intel.winRangeMin.toLocaleString()} - ${intel.winRangeMax.toLocaleString()} ${currency}` : 'N/A' },
        { label: 'Carrier interest', value: intel.carrierInterest },
        { label: 'Deadhead risk', value: intel.deadheadRisk },
        { label: 'Requirement norms', value: intel.accessorialNorms || 'N/A' },
        { label: 'Payment preference', value: intel.paymentPreference || 'N/A' },
        { label: 'Matching capacity', value: `${intel.topCarrierCount} open` },
        { label: 'Historical loads', value: `${intel.sampleSize}` }
      ]);
    } catch (error) {
      console.error('Error fetching lane intelligence:', error);
    }
  }

  private splitList(value?: string | null): string[] {
    return (value ?? '')
      .split(/[\n,]+/)
      .map(item => item.trim())
      .filter(Boolean);
  }

  private splitLines(value?: string | null): string[] {
    return (value ?? '')
      .split('\n')
      .map(item => item.trim())
      .filter(Boolean);
  }

  private cleanText(value: unknown): string | null {
    if (value == null) {
      return null;
    }
    const text = String(value).trim();
    return text.length ? text : null;
  }

  private toNumber(value: unknown): number | null {
    if (value == null || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private formatDate(value: unknown): string | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      return value.slice(0, 10);
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return null;
  }

  private formatTime(value: unknown): string | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      return value.slice(0, 5);
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`;
    }
    return null;
  }

  private formatWindow(start: unknown, end: unknown): string | null {
    const windowStart = this.formatTime(start);
    const windowEnd = this.formatTime(end);
    if (windowStart && windowEnd) {
      return `${windowStart}-${windowEnd}`;
    }
    return windowStart ?? windowEnd;
  }

  private display(value: unknown): string {
    const text = this.cleanText(value);
    return text ?? '-';
  }

  private displayList(values: string[]): string {
    return values.length ? values.join(', ') : '-';
  }
}
